import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";

export interface SavedJobInfo {
  id: string;
  job_id: string;
  created_at: string;
}

export function useSavedJobs() {
  const { user, isAuthenticated } = useAuth();
  const [savedJobIds, setSavedJobIds] = useState<Set<string>>(new Set());
  const [savedJobs, setSavedJobs] = useState<SavedJobInfo[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch all saved job IDs for the current user
  const fetchSavedJobs = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setSavedJobIds(new Set());
      setSavedJobs([]);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("vietnamcv_saved_jobs")
        .select("id, job_id, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Failed to fetch saved jobs:", error.message);
        return;
      }

      if (data) {
        setSavedJobs(data as SavedJobInfo[]);
        setSavedJobIds(new Set(data.map((item: SavedJobInfo) => item.job_id)));
      }
    } catch (err) {
      console.error("Failed to fetch saved jobs:", err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    fetchSavedJobs();
  }, [fetchSavedJobs]);

  // Check if a job is saved
  const isSaved = useCallback(
    (jobId: string) => savedJobIds.has(jobId),
    [savedJobIds],
  );

  // Toggle save/unsave
  const toggleSave = useCallback(
    async (jobId: string) => {
      if (!isAuthenticated || !user) return false;

      const currentlySaved = savedJobIds.has(jobId);

      if (currentlySaved) {
        // Unsave
        setSavedJobIds((prev) => {
          const next = new Set(prev);
          next.delete(jobId);
          return next;
        });
        setSavedJobs((prev) => prev.filter((j) => j.job_id !== jobId));

        const { error } = await supabase
          .from("vietnamcv_saved_jobs")
          .delete()
          .eq("user_id", user.id)
          .eq("job_id", jobId);

        if (error) {
          // Rollback on error
          setSavedJobIds((prev) => new Set(prev).add(jobId));
          console.error("Failed to unsave job:", error.message);
          return false;
        }
      } else {
        // Save
        setSavedJobIds((prev) => new Set(prev).add(jobId));

        const { error } = await supabase
          .from("vietnamcv_saved_jobs")
          .insert({ user_id: user.id, job_id: jobId })
          .select("id, job_id, created_at")
          .single();

        if (error) {
          setSavedJobIds((prev) => {
            const next = new Set(prev);
            next.delete(jobId);
            return next;
          });
          console.error("Failed to save job:", error.message);
          return false;
        }

        // Refresh list
        fetchSavedJobs();
      }

      return true;
    },
    [isAuthenticated, user, savedJobIds, fetchSavedJobs],
  );

  const savedCount = savedJobIds.size;

  return {
    savedJobIds,
    savedJobs,
    savedCount,
    loading,
    isSaved,
    toggleSave,
    fetchSavedJobs,
  };
}